// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## Write Intervals
//
// First play then click letters & accidentals to create an interval.

// Interval name type options:
//    longNames: Perfect 4th
//    genericNames: 4th
//    halfWholeNames: Chromatic Half Step, Whole Step, minor third, etc...
//    ascendingDescending: ascending P4
//      The default is: (intervalname) + above/below.

// Supported options:
//
//  grandStaff: boolean, default false
//    displays a grand staff, to allow for writing of compound intervals.
import { isClefName } from 'shared/definitions/musicTheory/clefs';
import type { IntervalName } from 'shared/definitions/musicTheory/intervals';
import type { NoteName } from 'shared/definitions/musicTheory/notesAndAccidentals';
import { isString } from 'shared/src/utils/isString';
import InteractivePrototype from './interactivePrototype';
import type { InteractiveOptions } from './interactivePrototype';

import SkillCalculator from '../uTheory.Skills/skillCalculator';
import utils from '../utils';
import QuestionGenerator from '../uTheory.Skills/questionGenerator';
import * as Skills from '../uTheory.Skills/skills';
import Note from '../uTheory.Notation/note';
import Interval from '../uTheory.Notation/interval';
import Clef from '../uTheory.Notation/clef';
import Staff from '../uTheory.Notation/staff';
import NoteHolder from '../uTheory.Notation/noteHolder';
import Editable from '../uTheory.Notation/editable';
import Measure from '../uTheory.Notation/measure';
import Voice from '../uTheory.Notation/voice';
import KeySignature from '../uTheory.Notation/keySignature';
import Music from '../uTheory.Notation/music';
import IntervalQuestion from '../uTheory.Skills/intervalQuestion';

interface WriteIntervalsOptions extends InteractiveOptions {
  lowestNote: string | Note;
  highestNote: string | Note;
  grandStaff: boolean;
  forceSkill?: string | number;
  /** Perfect 4th instead of P4 */
  longNames?: boolean;
  /** "half step" instead of m2 */
  halfWholeNames?: boolean;
  /** "2nd" instead of "M2" */
  genericNames?: boolean;
  /** use ascending and descending instead of "above" and "below" in the question prompt */
  ascendingDescending?: boolean;
  /** Generate only the direction (above/below) for the question prompt */
  directionOnly?: boolean;
}

interface WriteIntervalsQuestion {
  intervalQuestion: IntervalQuestion;
  text: string[];
  skills: string[];
}

/**
 * @typedef {object} IntervalQuestion
 * @prop {Note} note
 * @prop {Interval} interval
 * @prop {string[]} text
 * @prop {string[]} skills
 * @prop {string} clef
 */

class WriteIntervals extends InteractivePrototype {
  declare options: WriteIntervalsOptions;

  declare questions: WriteIntervalsQuestion[];

  declare currentQuestion: WriteIntervalsQuestion;

  skillCalculator: SkillCalculator;

  checkpointQuestionString?: string;

  /** The actual Clef object on the music object */
  clef: Clef;

  /** Holder for the given note */
  questionHolder: NoteHolder;

  /** Holder for the answer note */
  noteHolder: NoteHolder;

  /** Holder for the 2nd staff for the given note if we are in a grand staff */
  questionHolder2: NoteHolder;

  /** Holder for the second clef answer note, if we are in a grand staff */
  noteHolder2: NoteHolder;

  constructor(displayDiv, options) {
    super(displayDiv, options);
    this.targetSpeedFromSkills = WriteIntervals.registry
      .map((r) => r.skills)
      .reduce((a, b) => a.concat(b));
    this.settings = {
      instructions: {
        ariaLive: 'assertive',
        ariaLiveRegion: 'all',
      },
      editor: {
        fixedWidth: 180,
        ariaLive: 'polite',
        ariaTitle: {
          rhythm: false,
          keySignature: false,
          barline: false,
        },
      },
      letters: { letterButtons: false, accidentalButtons: true, checkButton: 'Check' },
      checkMark: {
        rightAnswerLength: 1,
        rightAnswerInOutRatio: 0.7,
        wrongAnswerLength: 1.0,
        wrongAnswerInOutRatio: 0.3,
      },
      player: {},
      oneTryOnly: false,
      progressTracker: true,
    };
    this.layout = {
      instructions: { percent: 30 },
      editor: { percent: 50 },
      letters: { percent: 20 },
    };
    this.init(displayDiv, options);
  }

  generateQuestions(questions?: string[]) {
    if (this.optionsTrue('questionPrompt')) {
      this.questionPrompt = this.options.questionPrompt;
    } else {
      this.questionPrompt =
        'Write the note<span style="font-size: 1.75em;"><br />{question} the given note</span>.';
    }

    let intervalQuestions: IntervalQuestion[];
    // Either generate or calculate questions:
    if (this.optionsTrue('drill')) {
      intervalQuestions = this.randomQuestionGenerator();
    } else {
      intervalQuestions = this.prepareManualQuestions(questions);
    }

    this.questions = this.processQuestions(intervalQuestions);

    if (!this.options.testMode) {
      this.questionPrompt += `<br />Number {question} of ${this.questions.length}`;
    }
  }

  randomQuestionGenerator(): IntervalQuestion[] {
    const generator = new QuestionGenerator();
    if (typeof this.options.lowestNote === 'undefined') {
      this.options.lowestNote = 'C2';
    }
    if (typeof this.options.highestNote === 'undefined') {
      this.options.highestNote = 'C6';
    }
    return generator.multipleSkillsGenerate(
      this.options.skills,
      this.options.maxQuestions || 8,
      this.options
    );
  }

  prepareManualQuestions(questions: string[]) {
    questions = questions || this.options.questions;
    return questions.map((question) => {
      const questionParts = question.split('/');
      // This will throw if the name's invalid, but that's what we want:
      const note = new Note(questionParts[0] as NoteName);
      // This will throw if the name's invalid, but that's what we want:
      const interval = new Interval(questionParts[1] as IntervalName);
      const clef = isClefName(questionParts[2]) ? questionParts[2] : undefined;
      return new IntervalQuestion(interval, note, { clef });
    }, this);
  }

  processQuestions(intervalQuestions: IntervalQuestion[]): WriteIntervalsQuestion[] {
    return intervalQuestions.map((intervalQuestion, questionNumber) => {
      const text = [this.questionText(intervalQuestion.interval), `${questionNumber + 1}`];
      const skills = this.calculateSkill(intervalQuestion.interval);

      if (!this.options.grandStaff) {
        const clef = intervalQuestion.prefersClef(intervalQuestion.clef);
        console.log(
          `Question ${questionNumber}: asked for ${intervalQuestion.clef}, but prefers ${clef}`
        );
        if (!clef) intervalQuestion.fixNoteRange();
        else intervalQuestion.clef = clef;
      } else {
        const fits = intervalQuestion.fitsGrandStaff();
        if (!fits) intervalQuestion.fixGrandStaffRange();
        const possibleClefs = intervalQuestion.note.prefersClef();
        const clef = new Clef(utils.randomElement(possibleClefs));
        intervalQuestion.clef = clef;
      }

      return {
        text,
        skills,
        intervalQuestion,
      };
    });
  }

  newCheckpointQuestion(question) {
    return this.loadingPromise.then(() => {
      this.checkpointQuestionString = question;
      this.questionNumber = 0;
      this.generateQuestions([question]);
      this.noteHolder.color = undefined;
      if (this.noteHolder2) this.noteHolder2.color = undefined;
      this.newQuestion();
      return Promise.resolve(this.currentQuestion);
    });
  }

  initializeMusic() {
    const clef = new Clef('treble');
    this.clef = clef;
    const staff = new Staff(clef);

    this.questionHolder = new NoteHolder(new Note(), 'h');
    this.noteHolder = new NoteHolder('', 'h');
    this.noteHolder.editable = new Editable(this.noteHolder, {
      pitch: true,
      accidental: false,
      deleteable: ['ghost'],
      selectable: true,
    });

    const measure = new Measure(this.questionHolder, this.noteHolder);

    const voice = new Voice(measure);
    voice.setStaff(staff);

    const keySig = new KeySignature('C', 'Major');

    if (this.options.grandStaff) {
      const clef2 = new Clef('bass');
      const staff2 = new Staff(clef2);
      this.questionHolder2 = new NoteHolder('', 'h');
      this.noteHolder2 = new NoteHolder('', 'h');
      this.noteHolder2.editable = new Editable(this.noteHolder2, {
        pitch: true,
        accidental: false,
        deleteable: ['ghost'],
        selectable: true,
      });
      this.noteHolder2.editable.link(this.noteHolder.editable);
      const measure2 = new Measure(this.questionHolder2, this.noteHolder2);
      const voice2 = new Voice(measure2);
      voice2.setStaff(staff2);
      this.music = new Music(staff, voice, staff2, voice2, keySig);
    } else {
      this.music = new Music(staff, voice, keySig);
    }
  }

  calculateSkill(interval: Interval) {
    if (typeof this.options.forceSkill !== 'undefined') {
      return [Skills.makeString(this.options.forceSkill)];
    }
    // otherwise, invoke a skillCalculator:
    if (typeof this.skillCalculator === 'undefined') {
      this.skillCalculator = new SkillCalculator();
    }

    if (this.optionsTrue('halfWholeNames')) {
      const halfWholeSkill = this.skillCalculator.getStaffHalfWholeSkill(interval);
      return [Skills.makeString(halfWholeSkill)];
    }
    const skills = this.skillCalculator.getStaffIntervalSkills(interval);
    return skills.map((s) => Skills.makeString(s)).filter(isString);
  }

  questionText(interval) {
    const intervalText = this.intervalText(interval);
    const directionText = this.directionText(interval);
    const ascDsc = this.options.ascendingDescending;

    if (this.options.directionOnly) {
      return directionText;
    }

    if (ascDsc) {
      return `${utils.aOrAn(directionText)} ${directionText} ${intervalText}`;
    }
    return `${utils.aOrAn(intervalText)} ${intervalText} <strong>${directionText}</strong>`;
  }

  intervalText(interval) {
    // longNames: Perfect 4th, minor 2nd, Augmented octave
    if (this.options.longNames) {
      return interval.longName();
    }
    if (this.options.halfWholeNames) {
      return interval.longHalfWholeName();
    }
    if (this.options.genericNames) {
      return interval.longGenericName();
    }
    return interval.longName();
  }

  directionText(interval) {
    if (this.options.ascendingDescending) {
      return interval.direction > 0 ? 'ascending' : 'descending';
    }
    return interval.direction > 0 ? 'above' : 'below';
  }

  newQuestion() {
    //      console.log(document.activeElement);
    this.letterPanel.clearNoteName();
    //      document.body.focus();
    this.editor.deselectAll();
    if (this.questionNumber > 0) {
      window.setTimeout(() => this.newQuestionFunction(), 1000);
      this.progressTracker.start(this.targetSpeed);
    } else this.newQuestionFunction();
  }

  newQuestionFunction() {
    this.questionHolder.length = 0;
    this.noteHolder.editable.eventHandler.setAccidental('');
    this.noteHolder.length = 0;
    this.noteHolder.editable.selected = false;

    this.currentQuestion = this.questions[this.questionNumber];

    if (this.options.grandStaff) {
      // Clear things out:
      this.questionHolder2.length = 0;
      this.noteHolder2.length = 0;
      this.noteHolder2.editable.eventHandler.setAccidental('');
      this.noteHolder2.editable.selected = false;

      // Set things up:
      if (this.currentQuestion.intervalQuestion.clef.equals('treble')) {
        this.questionHolder.notes[0] = this.currentQuestion.intervalQuestion.note;
      } else {
        this.questionHolder2.notes[0] = this.currentQuestion.intervalQuestion.note;
      }
    } else {
      this.questionHolder.notes[0] = this.currentQuestion.intervalQuestion.note;
      this.clef.setName(this.currentQuestion.intervalQuestion.clef);
    }
    this.editor.renderer.render();

    this.wrongAnswerCount = 0;
    super.newQuestion();
  }

  addListeners() {
    this.letterPanel.addAccidentalCallback(this.accidental, this);
    this.letterPanel.addCheckCallback(this.check, this);
    this.editor.addListener('contactEventEnd', this.wrongDirection, this);
  }

  accidental(accidental) {
    this.editor.setAccidental(accidental);
    this.clearWarning();
  }

  clearWarning() {
    this.checkMark.off();
  }

  /**
   * Tests if student has written an interval in the wrong direction and returns
   * true if they have.
   */
  wrongDirection() {
    const nh = this.options.grandStaff ? this.noteHolder.concat(this.noteHolder2) : this.noteHolder;
    const interval = this.currentQuestion.intervalQuestion.interval;
    const givenNote = this.currentQuestion.intervalQuestion.note;
    const answerNote = nh.notes[0];
    if (!answerNote) return false;
    if (interval.direction === 1 && answerNote.isLessThanIgnoreAccidental(givenNote)) {
      this.checkMark.warn('Careful: write an ascending, not descending interval.');
      return true;
    }
    if (interval.direction === -1 && answerNote.isGreaterThanIgnoreAccidental(givenNote)) {
      this.checkMark.warn('Careful: write a descending, not ascending interval.');
      return true;
    }
    return false;
  }

  check() {
    if (this.rightAnswerDelay) return;
    const nh = this.options.grandStaff ? this.noteHolder.concat(this.noteHolder2) : this.noteHolder;
    const givenNote = this.currentQuestion.intervalQuestion.note;
    const interval = this.currentQuestion.intervalQuestion.interval;
    const answerNote = nh.notes[0];

    // Be sure right # of notes are played:
    if (nh.isEmpty()) {
      this.checkMark.warn('You must draw a note first!');
      return;
    }
    if (this.wrongDirection() && !this.options.testMode) return;

    const goalNote = givenNote.getNoteAt(interval);

    // This should never happen...
    if (!goalNote) {
      if (this.options.testMode) {
        this.checkpointAnswer(true);
        return;
      }
      this.rightAnswer();

      throw new Error(`WriteIntervals asked an unanswerable question: ${givenNote} ${interval}`);
    }

    const correct = goalNote.equals(answerNote);

    if (this.options.testMode) {
      this.checkpointAnswer(correct);
      return;
    }

    if (correct) {
      this.rightAnswer(1000);
    } else {
      const goalSemiTones = interval.getSemiTones();
      const actualSemiTones = Math.abs(answerNote.getMidiNumber() - givenNote.getMidiNumber());

      if (this.wrongAnswerCount === 0) {
        this.wrongAnswer();
      } else if (goalNote.equalsIgnoreAccidental(answerNote)) {
        this.wrongAnswer('Just fix the accidental!');
      } else if (goalNote.equalsIgnoreOctave(answerNote)) {
        this.wrongAnswer('Just fix the octave');
      } else if (
        // too big:
        actualSemiTones > goalSemiTones
      ) {
        this.wrongAnswer('Your interval is too big!');
      } else if (actualSemiTones < goalSemiTones) {
        this.wrongAnswer('Your interval is too small!');
      } else {
        this.wrongAnswer('Right number of half steps, but wrong letter spelling!');
      }
      this.wrongAnswerCount++;
    }
  }

  getAnswerAndData(correct) {
    const correctAnswer = this.currentQuestion.intervalQuestion.note.getNoteAt(
      this.currentQuestion.intervalQuestion.interval
    );
    const questionText = `Write the note ${this.currentQuestion.text[0]} the given note.`;

    const nh = this.options.grandStaff ? this.noteHolder.concat(this.noteHolder2) : this.noteHolder;
    const studentAnswerText = nh.notes[0] ? nh.notes[0].toShortString() : '__';

    this.editor.deselectAll();
    if (!correct) {
      this.noteHolder.color = 'red';
      (this.noteHolder2 || { color: 'red' }).color = 'red';
    }

    this.editor.renderer.render();

    const answerAndData = {
      question: this.checkpointQuestionString,
      questionType: 'WriteIntervals',
      questionText,
      questionSVG: null,
      studentAnswerSVG: this.editor.exportSVG(),
      studentAnswerText,
      correctAnswerText: (correctAnswer || new Note()).toShortString(),
      rightAnswers: correct ? 1 : 0,
      wrongAnswers: correct ? 0 : 1,
      skills: this.questions[this.questionNumber].skills,
    };

    return answerAndData;
  }

  debugRightAnswer() {
    this.rightAnswer();
  }

  gameOver() {
    this.instructionsDiv.innerHTML = 'Nice work!';
    this.instructionsDiv.setAttribute('role', 'alert');
    this.displayDiv.removeChild(this.lettersDiv);
    delete this.noteHolder.editable;
    this.editor.renderer.render();
    super.gameOver();
  }

  playAnswer() {
    const { note, interval } = this.currentQuestion.intervalQuestion;
    const answerNote = note.getNoteAt(interval);
    this.player.play(note, { startTime: 0, stopTime: 0.5 });
    this.player.play(answerNote || new Note(), { startTime: 0.5, stopTime: 1.25 });
  }

  static registry = [
    {
      description: 'Write Intervals',
      skills: [
        'ascending_major_perfect',
        'ascending_others',
        'descending_minor_perfect',
        'descending_others',
      ],
    },
    {
      description: 'Write Half & Whole Steps',
      skills: ['ws', 'chs', 'dhs'],
      options: {
        halfWholeNames: true,
      },
    },
  ];
}

export default WriteIntervals;
